 
import React from "react";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { ResponsiveCommandMultiSelect } from "@/components/ui/responsive-select/ResponsiveCommandMultiSelect";
import type { SimpleFieldConfig } from "../types";
import type { FilterState } from "@/components/shared/types";
import { toCommandOptions } from "./filter-rendering-helpers";

interface OptionsFilterProps {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
}

/** Shared multi-select renderer for tags, json, and regular option fields. */
function MultiSelectFilter({ fieldId, fieldConfig, filters, onFilterChange }: OptionsFilterProps) {
  const selectedValues: string[] = Array.isArray(filters[fieldId])
    ? (filters[fieldId])
    : filters[fieldId]
    ? [String(filters[fieldId])]
    : [];

  return (
    <div key={fieldId} className="w-full overflow-visible">
      <ResponsiveCommandMultiSelect
        value={selectedValues}
        onValueChange={(values) => {
          if (values.length > 3) return;
          const newFilters = { ...filters };
          if (values.length === 0) {
            delete newFilters[fieldId];
          } else {
            newFilters[fieldId] = values;
          }
          onFilterChange(newFilters);
        }}
        placeholder={`All ${fieldConfig.label}`}
        className="w-full"
        highZIndex={true}
        expandInline={true}
        expandInlineVariant="replace"
        title={`Filter by ${fieldConfig.label}`}
        options={(fieldConfig.options ?? []).map((option) => ({
          value: String(option.value),
          label: option.label,
          searchableText: `${option.label} ${option.value}`.trim(),
        }))}
      />
    </div>
  );
}

export function TagsOrJsonFilter(props: OptionsFilterProps) {
  return <MultiSelectFilter {...props} />;
}

export function OptionsMultiSelectFilter(props: OptionsFilterProps) {
  return <MultiSelectFilter {...props} />;
}

export function BooleanFilter({ fieldId, fieldConfig, filters, onFilterChange }: OptionsFilterProps) {
  // Special handling for is_approved: includes a "disapproved" option
  if (fieldId === "is_approved") {
    return (
      <div key={fieldId} className="w-full overflow-visible">
        <ResponsiveCommandSelect
          value={
            filters[fieldId] === undefined
              ? "all"
              : filters[fieldId] === "disapproved"
              ? "disapproved"
              : filters[fieldId]
              ? "true"
              : "false"
          }
          onValueChange={(value) => {
            const newFilters = { ...filters };
            if (value === "all") {
              delete newFilters[fieldId];
              delete newFilters.sync_status;
            } else if (value === "disapproved") {
              newFilters[fieldId] = "disapproved";
            } else {
              newFilters[fieldId] = value === "true";
              if (newFilters.sync_status === "disapproved") {
                delete newFilters.sync_status;
              }
            }
            onFilterChange(newFilters);
          }}
          placeholder={fieldConfig.label}
          className="w-full"
          highZIndex={true}
          expandInline={true}
          expandInlineVariant="replace"
          options={toCommandOptions([
            { value: "all", label: "All Approved" },
            { value: "true", label: "Approved" },
            { value: "false", label: "Pending" },
          ])}
        />
      </div>
    );
  }

  // Regular boolean fields
  return (
    <div key={fieldId} className="w-full overflow-visible">
      <ResponsiveCommandSelect
        value={filters[fieldId] === undefined ? "all" : filters[fieldId] ? "true" : "false"}
        onValueChange={(value) => {
          const newFilters = { ...filters };
          if (value === "all") {
            delete newFilters[fieldId];
          } else {
            newFilters[fieldId] = value === "true";
          }
          onFilterChange(newFilters);
        }}
        placeholder={fieldConfig.label}
        className="w-full"
        highZIndex={true}
        expandInline={true}
        expandInlineVariant="replace"
        options={toCommandOptions([
          { value: "all", label: `All ${fieldConfig.label}` },
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ])}
      />
    </div>
  );
}
